import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

const TIERS = ['L1', 'L2', 'L3', 'Other'];
const ALL_CAT = '__all__';

const TIER_COLORS = {
  L1: { bg: 'var(--action-bg)', color: 'var(--status-resolved-text)', border: 'var(--action-hover)' },
  L2: { bg: 'var(--blue-100)', color: 'var(--blue-700)', border: 'var(--blue-200)' },
  L3: { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
  Other: { bg: 'var(--n-100)', color: 'var(--text-muted)', border: 'var(--border-subtle)' },
};

const EMPTY = { name: '', email: '', password: '', role: 'agent', agent_products: [], custom_role_id: '' };

export default function SupportUsers() {
  const [agents, setAgents]         = useState([]);
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  useEffect(() => { apiFetch('/roles').then(d => setCustomRoles(d.custom || [])).catch(() => {}); }, []);
  const [activeCat, setActiveCat]   = useState(ALL_CAT);
  const [loading, setLoading]       = useState(true);

  const [expandedId, setExpandedId] = useState(null);
  const [modal, setModal]       = useState(null); // null | 'add' | agent-object
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Import state
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const [pwdTarget, setPwdTarget]       = useState(null);
  const [newPwd, setNewPwd]             = useState('');
  const [pwdSaving, setPwdSaving]       = useState(false);
  const [pwdError, setPwdError]         = useState('');
  const [pwdDone, setPwdDone]           = useState(false);

  const loadAgents = useCallback(async () => {
    const data = await apiFetch('/users/agents?includeInactive=1');
    setAgents(data);
  }, []);

  const loadProducts = useCallback(async () => {
    const data = await apiFetch('/products');
    setProducts(data);
  }, []);

  const loadCategories = useCallback(async () => {
    const data = await apiFetch('/products/categories');
    setCategories(data);
  }, []);

  useEffect(() => {
    Promise.all([loadAgents(), loadProducts(), loadCategories()]).finally(() => setLoading(false));
  }, [loadAgents, loadProducts, loadCategories]);

  // ── helpers ──────────────────────────────────────────────
  function productName(p) {
    return p.name + (p.manufacturer ? ` — ${p.manufacturer}` : '') + (p.model ? ` ${p.model}` : '');
  }

  // Returns [{product, tier}] for display
  function agentProductEntries(agent) {
    const entries = Array.isArray(agent.agent_products) ? agent.agent_products : [];
    return entries.map(e => {
      const prod = products.find(x => x.id === e.product_id);
      return prod ? { name: prod.name, tier: e.tier || 'L1' } : null;
    }).filter(Boolean);
  }

  // ── form product helpers ──────────────────────────────────
  function getProductTier(productId) {
    const entry = form.agent_products.find(p => p.product_id === productId);
    return entry ? entry.tier : null; // null = not selected
  }

  function toggleProduct(productId) {
    setForm(f => {
      const exists = f.agent_products.find(p => p.product_id === productId);
      if (exists) {
        return { ...f, agent_products: f.agent_products.filter(p => p.product_id !== productId) };
      }
      return { ...f, agent_products: [...f.agent_products, { product_id: productId, tier: 'L1' }] };
    });
  }

  function setProductTier(productId, tier) {
    setForm(f => ({
      ...f,
      agent_products: f.agent_products.map(p =>
        p.product_id === productId ? { ...p, tier } : p
      ),
    }));
  }

  // ── modal helpers ─────────────────────────────────────────
  function openAdd() {
    setForm(EMPTY);
    setError('');
    setModal('add');
  }

  function openEdit(agent) {
    setForm({
      name: agent.name,
      email: agent.email,
      password: '',
      role: agent.role || 'agent',
      custom_role_id: agent.custom_role_id || '',
      agent_products: Array.isArray(agent.agent_products)
        ? agent.agent_products.map(p =>
            typeof p === 'string' ? { product_id: p, tier: 'L1' } : p
          )
        : [],
    });
    setError('');
    setModal(agent);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (modal !== 'add' && !payload.password) delete payload.password;
      if (modal === 'add') {
        await apiFetch('/users/agents', { method: 'POST', body: payload });
      } else {
        await apiFetch(`/users/agents/${modal.id}`, { method: 'PATCH', body: payload });
      }
      setModal(null);
      loadAgents();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openResetPwd(agent) {
    setPwdTarget(agent);
    setNewPwd('');
    setPwdError('');
    setPwdDone(false);
  }

  async function handleResetPwd(e) {
    e.preventDefault();
    if (!newPwd.trim() || newPwd.length < 6) { setPwdError('Password must be at least 6 characters'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await apiFetch(`/users/agents/${pwdTarget.id}`, { method: 'PATCH', body: { password: newPwd } });
      setPwdDone(true);
      setNewPwd('');
      setTimeout(() => setPwdTarget(null), 1500);
    } catch (err) {
      setPwdError(err.message);
    } finally {
      setPwdSaving(false);
    }
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = sessionStorage.getItem('kgreen_token');
      const res = await fetch('/api/users/agents/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Import failed');
      setImportResult({ ...json, ok: true });
      if (json.inserted > 0) loadAgents();
    } catch (err) {
      setImportResult({ ok: false, error: err.message });
    } finally {
      setImporting(false);
    }
  }

  async function confirmDelete() {
    try {
      const r = await apiFetch(`/users/agents/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      loadAgents();
      if (r.open_tickets) {
        const where = r.action === 'auto_reassign'
          ? `${r.reassigned} reassigned${r.to_admin_queue ? `, ${r.to_admin_queue} sent to admin queue` : ''}`
          : `${r.to_admin_queue} sent to admin queue`;
        alert(`User deactivated. ${r.open_tickets} open ticket(s): ${where}.`);
      }
    } catch (err) {
      alert('Deactivate failed: ' + err.message);
    }
  }

  async function reactivate(agent) {
    try {
      await apiFetch(`/users/agents/${agent.id}/reactivate`, { method: 'POST' });
      loadAgents();
    } catch (err) { alert('Reactivate failed: ' + err.message); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Support Users</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 }}>{agents.length} agent{agents.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ accentColor: '#0B6623' }} />
            Show deactivated
          </label>
          <a
            href="/kgreen-agents-import-template.xlsx"
            download="kgreen-agents-import-template.xlsx"
            className="btn btn-secondary"
            style={{ fontSize: 13 }}
          >⬇ Template</a>
          <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? 'Importing…' : '📥 Import Excel'}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
          <button className="btn btn-primary" onClick={openAdd}>+ Add Support User</button>
        </div>
      </div>

      {/* Table */}
      {agents.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
          <p>No support users yet.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openAdd}>Add First Support User</button>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                {['Name', 'Email / Login', 'Role', 'Open Tickets', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.filter(a => showInactive ? true : a.active !== 0).map(agent => {
                const entries = agentProductEntries(agent);
                const isExpanded = expandedId === agent.id;
                const inactive = agent.active === 0;
                const today = new Date().toISOString().slice(0, 10);
                const oof = agent.oof_from && agent.oof_to && today >= agent.oof_from && today <= agent.oof_to;
                return (
                  <React.Fragment key={agent.id}>
                    <tr
                      style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', background: isExpanded ? 'var(--action-bg)' : 'var(--bg-surface)', transition: 'background .1s', opacity: oof ? 0.5 : 1 }}
                      onClick={() => setExpandedId(isExpanded ? null : agent.id)}
                      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-app)'; }}
                      onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                    >
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--status-resolved-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'var(--status-resolved-text)', flexShrink: 0 }}>
                            {agent.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div>{agent.name}
                              {oof && <span title={`Out of office ${agent.oof_from} → ${agent.oof_to}`} style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, background: 'var(--amber-100)', color: '#92400e', padding: '2px 8px', borderRadius: 999 }}>🌴 OOF</span>}
                              {inactive && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, background: 'var(--n-100)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 999 }}>DEACTIVATED</span>}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>{entries.length} product{entries.length !== 1 ? 's' : ''} assigned</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-secondary)' }}>{agent.email}</td>
                      <td style={{ padding: '12px 14px' }}><RoleBadge role={agent.role} /></td>
                      <td style={{ padding: '12px 14px' }}><OpenTicketCount agentId={agent.id} /></td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginRight: 8, userSelect: 'none' }}>{isExpanded ? '▲' : '▼'}</span>
                        <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => openEdit(agent)}>Edit</button>
                        {inactive ? (
                          <button className="btn btn-sm" style={{ background: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', border: '1px solid #bbf7d0' }} onClick={() => reactivate(agent)}>↺ Reactivate</button>
                        ) : (
                          <>
                            <button className="btn btn-sm" style={{ marginRight: 6, background: 'var(--amber-50)', color: '#92400e', border: '1px solid var(--amber-100)' }} onClick={() => openResetPwd(agent)}>🔑 Reset Pwd</button>
                            <button className="btn btn-sm" style={{ background: 'var(--red-50)', color: '#b91c1c' }} onClick={() => setDeleteTarget(agent)}>Deactivate</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td colSpan={5} style={{ padding: '0 14px 14px 58px', background: 'var(--action-bg)' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-resolved-text)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Products &amp; Tiers</div>
                          {entries.length === 0 ? (
                            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No products assigned</span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {entries.map(e => {
                                const tc = TIER_COLORS[e.tier] || TIER_COLORS.Other;
                                return (
                                  <span key={e.name + e.tier} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 7, padding: '4px 10px', fontSize: 12 }}>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{e.name}</span>
                                    <span style={{ background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 700, letterSpacing: .3 }}>{e.tier}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="card" style={{ width: '100%', maxWidth: 580, padding: 32, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>{modal === 'add' ? 'Add Support User' : `Edit — ${modal.name}`}</h2>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

            <form onSubmit={handleSave}>
              {/* Basic details */}
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10 }}>User Details</p>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: 12 }}>Full Name *</label>
                  <input className="input" style={{ fontSize: 13, padding: '7px 10px' }} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: 12 }}>Email / Login *</label>
                  <input className="input" type="email" style={{ fontSize: 13, padding: '7px 10px' }} value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label" style={{ fontSize: 12 }}>
                    Password {modal !== 'add' && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(leave blank to keep current)</span>}
                    {modal === 'add' && ' *'}
                  </label>
                  <input className="input" type="password" style={{ fontSize: 13, padding: '7px 10px' }} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required={modal === 'add'} placeholder={modal !== 'add' ? '••••••••' : ''} />
                </div>
              </div>

              {/* Role */}
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10 }}>Base Role</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {[
                    { value: 'agent', label: 'Support Agent', desc: 'Handles assigned tickets only', icon: '🎧' },
                    { value: 'manager', label: 'Support Manager', desc: 'Views & assigns all tickets', icon: '🛡️' },
                    { value: 'admin', label: 'Administrator', desc: 'Full system access', icon: '👑' },
                  ].map(opt => {
                    const active = form.role === opt.value;
                    return (
                      <button key={opt.value} type="button"
                        onClick={() => setForm(f => ({ ...f, role: opt.value }))}
                        style={{
                          padding: '12px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                          border: `2px solid ${active ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`,
                          background: active ? 'var(--action-bg)' : 'var(--bg-surface)',
                          transition: 'all .15s',
                        }}>
                        <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.icon}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: active ? 'var(--status-resolved-text)' : 'var(--text-main)', marginBottom: 2 }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
                {/* Role selector — built-in (sets base role) or custom (adds privileges) */}
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6, display: 'block', marginBottom: 6 }}>Role</label>
                  <select className="input"
                    value={form.custom_role_id ? form.custom_role_id : `base:${form.role}`}
                    onChange={e => {
                      const v = e.target.value;
                      if (v.startsWith('base:')) setForm(f => ({ ...f, role: v.slice(5), custom_role_id: '' }));
                      else setForm(f => ({ ...f, custom_role_id: v }));
                    }}>
                    <optgroup label="Built-in roles">
                      <option value="base:agent">Support Agent</option>
                      <option value="base:manager">Support Manager</option>
                      <option value="base:admin">Administrator</option>
                    </optgroup>
                    {customRoles.length > 0 && (
                      <optgroup label="Custom roles">
                        {customRoles.map(r => <option key={r.id} value={r.id}>{r.name} (custom)</option>)}
                      </optgroup>
                    )}
                  </select>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                    A built-in role sets the user's base access. A custom role adds its privileges (Maintenance → Roles &amp; Privileges) on top of the base role above.
                  </p>
                </div>
              </div>

              {/* Products + per-product tier — category filtered */}
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6 }}>
                    Products &amp; Support Tier
                  </p>
                  {form.agent_products.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--status-resolved-text)', fontWeight: 600 }}>
                      {form.agent_products.length} assigned
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  {form.role === 'manager'
                    ? 'Optionally assign products to indicate areas of oversight.'
                    : 'Select a category, tick products, then set the tier for each.'}
                </p>

                {products.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No products added yet — add them in the Products tab first.</p>
                ) : (
                  <>
                    {/* ── Category tab strip ── */}
                    <div style={{
                      display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12,
                      padding: '8px 10px', background: 'var(--bg-surface)', borderRadius: 8,
                      border: '1px solid var(--border-subtle)',
                    }}>
                      {/* "All" tab */}
                      {[{ id: ALL_CAT, name: 'All' }, ...categories].map(cat => {
                        const isActive = activeCat === cat.id;
                        // count assigned products in this category
                        const assignedCount = cat.id === ALL_CAT
                          ? form.agent_products.length
                          : form.agent_products.filter(ap =>
                              products.find(p => p.id === ap.product_id)?.category_id === cat.id
                            ).length;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setActiveCat(cat.id)}
                            style={{
                              padding: '4px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: isActive ? 700 : 500,
                              border: `1.5px solid ${isActive ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`,
                              background: isActive ? 'var(--status-resolved-text)' : 'var(--bg-surface)',
                              color: isActive ? '#fff' : 'var(--text-secondary)',
                              cursor: 'pointer', transition: 'all .12s',
                              display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                            {cat.name}
                            {assignedCount > 0 && (
                              <span style={{
                                background: isActive ? 'rgba(255,255,255,.3)' : 'var(--action-bg)',
                                color: isActive ? '#fff' : '#094d1e',
                                borderRadius: 999, padding: '0 5px', fontSize: 10, fontWeight: 700,
                              }}>{assignedCount}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* ── Product list for selected category ── */}
                    {(() => {
                      const visible = activeCat === ALL_CAT
                        ? products
                        : products.filter(p => p.category_id === activeCat);
                      if (visible.length === 0) return (
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0' }}>
                          No products in this category.
                        </p>
                      );
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                          {visible.map(p => {
                            const currentTier = getProductTier(p.id);
                            const isChecked = currentTier !== null;
                            return (
                              <div key={p.id} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '8px 12px', borderRadius: 8,
                                border: `1px solid ${isChecked ? 'var(--action-hover)' : 'var(--border-subtle)'}`,
                                background: isChecked ? 'var(--action-bg)' : 'var(--bg-surface)',
                                transition: 'all .12s',
                              }}>
                                {/* Checkbox + names */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', minWidth: 0 }}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleProduct(p.id)}
                                    style={{ accentColor: '#0B6623', flexShrink: 0 }}
                                  />
                                  <div style={{ minWidth: 0 }}>
                                    <span style={{
                                      fontSize: 13, fontWeight: isChecked ? 600 : 400,
                                      color: isChecked ? 'var(--status-resolved-text)' : 'var(--text-muted)',
                                      display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                      {productName(p)}
                                    </span>
                                    {activeCat === ALL_CAT && p.category_name && (
                                      <span style={{ fontSize: 10.5, color: '#94a3b8' }}>{p.category_name}</span>
                                    )}
                                  </div>
                                </label>

                                {/* Tier pills — only when checked */}
                                {isChecked && (
                                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    {TIERS.map(tier => {
                                      const active = currentTier === tier;
                                      const tc = TIER_COLORS[tier];
                                      return (
                                        <button key={tier} type="button"
                                          onClick={() => setProductTier(p.id, tier)}
                                          style={{
                                            padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                                            border: `1.5px solid ${active ? tc.border : 'var(--n-300)'}`,
                                            background: active ? tc.bg : 'var(--bg-surface)',
                                            color: active ? tc.color : 'var(--text-secondary)',
                                            cursor: 'pointer', transition: 'all .1s',
                                          }}>
                                          {tier}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : (modal === 'add' ? 'Create User' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {pwdTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setPwdTarget(null); }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700 }}>Reset Password</h2>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {pwdTarget.name} · <RoleBadge role={pwdTarget.role} />
                </p>
              </div>
              <button onClick={() => setPwdTarget(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>

            {pwdDone ? (
              <div style={{ background: 'var(--action-bg)', color: 'var(--status-resolved-text)', border: '1px solid var(--action-hover)', borderRadius: 8, padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
                ✓ Password reset successfully
              </div>
            ) : (
              <form onSubmit={handleResetPwd}>
                {pwdError && (
                  <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{pwdError}</div>
                )}
                <div style={{ marginBottom: 20 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 4 }}>New Password *</label>
                  <input
                    className="input"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newPwd}
                    onChange={e => { setNewPwd(e.target.value); setPwdError(''); }}
                    autoFocus
                    required
                  />
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 5 }}>
                    The user will use this password on their next login.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setPwdTarget(null)}>Cancel</button>
                  <button className="btn btn-primary" disabled={pwdSaving}>
                    {pwdSaving ? 'Saving…' : 'Set New Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Import result modal */}
      {importResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}>
          <div className="card" style={{ width: '100%', maxWidth: 480, padding: 32 }}>
            {importResult.ok ? (
              <>
                <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>✅</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>Import Complete</h2>
                <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: importResult.skipped > 0 ? 8 : 0 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Support users added</span>
                    <span style={{ fontWeight: 700, color: 'var(--status-resolved-text)' }}>{importResult.inserted}</span>
                  </div>
                  {importResult.skipped > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Skipped (email already exists)</span>
                      <span style={{ fontWeight: 700, color: '#92400e' }}>{importResult.skipped}</span>
                    </div>
                  )}
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c', marginBottom: 8 }}>⚠ Errors ({importResult.errors.length})</p>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#7f1d1d', maxHeight: 150, overflowY: 'auto' }}>
                      {importResult.errors.map((e, i) => <li key={i}><strong>{e.row}</strong>: {e.error}</li>)}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>❌</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 16 }}>Import Failed</h2>
                <p style={{ color: '#b91c1c', textAlign: 'center', fontSize: 14, marginBottom: 20 }}>{importResult.error}</p>
              </>
            )}
            <div style={{ textAlign: 'center' }}>
              <button className="btn btn-primary" onClick={() => setImportResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div className="card" style={{ padding: 32, maxWidth: 400, width: '100%' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Deactivate support user?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              <strong>{deleteTarget.name}</strong> will no longer be able to log in and will be hidden from lists and assignment. Their open tickets are handled per your setting (admin queue or auto-reassign); <strong>closed tickets keep their name and history</strong>. You can reactivate them later.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete}>Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    agent:   { bg: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', border: 'var(--status-resolved-text)', label: '🎧 Agent' },
    manager: { bg: 'var(--blue-50)', color: 'var(--blue-700)', border: 'var(--blue-200)', label: '🛡️ Manager' },
  };
  const s = styles[role] || styles.agent;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

// Small component to show live open ticket count per agent
function OpenTicketCount({ agentId }) {
  const [count, setCount] = useState('…');
  useEffect(() => {
    apiFetch(`/tickets?limit=200`)
      .then(data => {
        const open = (data.tickets || []).filter(
          t => t.assigned_to === agentId && !['resolved', 'closed'].includes(t.status)
        ).length;
        setCount(open);
      })
      .catch(() => setCount('—'));
  }, [agentId]);
  return (
    <span style={{ background: count > 0 ? 'var(--amber-100)' : 'var(--n-100)', color: count > 0 ? '#92400e' : 'var(--text-secondary)', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500 }}>
      {count} open
    </span>
  );
}
